import React from 'react';
import { LS_KEY_LAST_ACCOUNT } from 'src/constants/localStorageKeys';

/**
 * The account last signed in on this browser, for "Continue as". Only what
 * the card shows: no token, so it signs nobody in by itself and survives
 * sign-out on purpose. Landing reads and writes the same key.
 */
export interface LastAccount {
  email: string;
  first_name: string | null;
  picture: string | null;
}

type AccountFields = {
  email?: string | null;
  first_name?: string | null;
  picture?: string | null;
};

const CHANGE_EVENT = 'abekus:last-account';

export const readLastAccount = (): LastAccount | null => {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_KEY_LAST_ACCOUNT) || 'null');
    return raw && typeof raw.email === 'string' && raw.email ? raw : null;
  } catch {
    return null;
  }
};

const write = (account: LastAccount | null) => {
  try {
    if (account) {
      localStorage.setItem(LS_KEY_LAST_ACCOUNT, JSON.stringify(account));
    } else {
      localStorage.removeItem(LS_KEY_LAST_ACCOUNT);
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    // Blocked storage: the card just doesn't show next time.
  }
};

export const rememberAccount = (user: AccountFields | null | undefined) => {
  if (!user?.email) return;
  write({
    email: user.email,
    first_name: user.first_name || null,
    picture: user.picture || null,
  });
};

export const forgetLastAccount = () => write(null);

/**
 * `kshi•••@abekus.ai`: enough for the owner to tell two accounts apart, not
 * enough to read the address. Landing and the extension mask the same way,
 * which is how a card shown twice is recognised as one account.
 */
export const maskEmail = (email: string | null | undefined): string | null => {
  const [name, domain] = String(email || '').split('@');
  if (!name || !domain) return null;
  const keep = Math.min(4, Math.max(1, name.length - 2));
  return `${name.slice(0, keep)}•••@${domain}`;
};

/**
 * sha256 (hex) of the normalised address: how this page and the extension
 * recognise one account without either showing the other an address. Null
 * where the browser has no SubtleCrypto (non-https origins).
 */
export const emailHash = async (
  email: string | null | undefined,
): Promise<string | null> => {
  const value = String(email || '')
    .trim()
    .toLowerCase();
  if (!value || typeof crypto === 'undefined' || !crypto.subtle) return null;
  try {
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(value),
    );
    return [...new Uint8Array(digest)]
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
};

/** `emailHash` of an address, for render code. */
export const useEmailHash = (email: string | null | undefined) => {
  const [hash, setHash] = React.useState<string | null>(null);
  React.useEffect(() => {
    let live = true;
    emailHash(email).then((value) => {
      if (live) setHash(value);
    });
    return () => {
      live = false;
    };
  }, [email]);
  return hash;
};

export const useLastAccount = (): LastAccount | null => {
  const [account, setAccount] = React.useState(readLastAccount);
  React.useEffect(() => {
    const sync = () => setAccount(readLastAccount());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return account;
};
